import * as PropTypes from "prop-types"
import { useContext, useState } from "react"
import styled from "styled-components"
import { useLocale, useT } from "react-polyglot-hooks"
import isEmpty from "lodash/isEmpty"
import dynamic from "next/dynamic"
import createNewOpportunity from "helpers/newOpportunity"
import { validateForm } from "helpers/validators"
import { PHONE_PREFIX } from "helpers/constants"
import { useRouter } from "next/router"
import { CALL_FORM_ID, CAR_FORM_ID, sendEvent } from "scripts/GoogleAnalytics"
import AnimatedLoader from "components/AnimatedLoader"
import Col from "../../styled/Grid/Col"
import { Row } from "../../styled/Grid"
import BasicInput from "../inputs/BasicInput"
import CheckBoxField from "../inputs/CheckBoxField"
import Typography from "../../styled/Typography"
import Icon from "../../icons/icon"
import { Support } from "../../icons/support"
import { PrimaryButton } from "../../styled/Buttons/PrimaryButton"
import { ModalContext } from "../../Modals/GlobalModals/ModalContext"
import JobTypeSelector from "../inputs/JobTypeSelector"
import TermsAndPrivacyField from "../inputs/TermsAndPrivacyField"

const CallMeFormCompleted = dynamic(() => import("../CallMeFormCompleted"))
const Notifications = dynamic(() => import("../../Notifications"))

export const ButtonContainer = styled(Col)`
  padding: 25px;
`

const Wrapper = styled.div`
  max-width: 325px;
  margin: 0 auto;
`

const getInputs = (showEmail, showJobType) => ({
  first_name: { required: true, type: "text" },
  last_name: { required: true, type: "text" },
  email: { required: showEmail, type: "email" },
  job_type: { required: showJobType, type: "text" },
  phone: { required: true, type: "phone" }
})

function CallMeForm({
  formName,
  handleClose,
  handleLoading,
  handleCompleted,
  showEmail,
  showJobType,
  extraData,
  showSubmit,
  cta,
  whiteLinks,
  initialData,
  submitClassName
}) {
  const t = useT()
  const locale = useLocale()
  const router = useRouter()
  const { showModal } = useContext(ModalContext)

  const [formState, setFormState] = useState(showJobType ? { ...initialData, job_type: "employee" } : initialData)
  const [completed, setCompleted] = useState(false)
  const [isFetching, setIsFetching] = useState(false)
  const [errors, setErrors] = useState({})
  const [notification, setNotification] = useState(null)

  const { first_name, last_name, email, phone, job_type, accepts_offers, accepts_conditions } = formState

  const onSuccess = (data = {}) => {
    setErrors({})
    setIsFetching(false)
    setCompleted(true)

    if (formName === "car_data") sendEvent("conversion", { send_to: CAR_FORM_ID })
    else sendEvent("conversion", { send_to: CALL_FORM_ID })

    if (formName === "car_data") {
      if (handleCompleted) handleCompleted(data)
    } else if (locale === "it") {
      showModal("crif", {
        id: data.id,
        page: formName,
        onClose: handleCompleted && (() => handleCompleted(data)),
        accepts_whatsapp: data.accepts_whatsapp,
        last_web_leasing_request_id: data.last_web_leasing_request_id
      })
    } else if (locale === "es" && !data.accepts_whatsapp) {
      showModal("whatsappConsent", {
        id: data.id,
        page: formName,
        onClose: handleCompleted && (() => handleCompleted(data)),
        last_web_leasing_request_id: data.last_web_leasing_request_id
      })
    } else {
      if (data.last_web_leasing_request_id) router.push(`/${locale}/online/${data.last_web_leasing_request_id}/empezar`)
      if (handleCompleted) handleCompleted(data)
    }
  }

  const onError = err => {
    setIsFetching(false)
    setErrors(err)
    if (handleLoading) handleLoading(false)
  }

  const submitForm = e => {
    e.preventDefault()
    if (isFetching) return
    const err = validateForm(formState, getInputs(showEmail, showJobType), t)
    setErrors(err)
    if (isEmpty(err)) {
      if (!formState.accepts_conditions) {
        setNotification(t("common.form.accept_terms"))
        return
      }
      setIsFetching(true)
      if (handleLoading) handleLoading(true)
      createNewOpportunity({ ...formState, ...extraData }, formName, locale)
        .then(async (data) => {
          onSuccess({ ...data, ...formState, ...extraData })
        })
        .catch(onError)
    }
  }

  const restartForm = () => {
    if (handleClose) {
      handleClose()
    } else {
      setCompleted(false)
      setFormState({})
    }
  }

  const onChange = (field, val) => {
    setFormState(s => ({ ...s, [field]: val }))
  }

  if (completed) return <CallMeFormCompleted handleClose={restartForm} extraData={job_type} />

  return (
    <Wrapper>
      <form onSubmit={submitForm} id="callMeForm" noValidate>
        <Row>
          <Col xs={12}>
            <BasicInput
              name="first_name"
              value={first_name}
              placeholder={t("common.form.name")}
              handleChange={onChange}
              errors={errors}
              formName={formName}
              transparent={false}
              noScroll
            />
          </Col>
          <Col xs={12}>
            <BasicInput
              name="last_name"
              value={last_name}
              placeholder={t("common.form.last_name")}
              handleChange={onChange}
              errors={errors}
              formName={formName}
              transparent={false}
              noScroll
            />
          </Col>
          {showEmail && (
            <Col xs={12}>
              <BasicInput
                type="email"
                name="email"
                value={email}
                placeholder={t("common.form.email")}
                handleChange={onChange}
                errors={errors}
                formName={formName}
                transparent={false}
                noScroll
              />
            </Col>
          )}
          <Col xs={12}>
            <BasicInput
              type="phone"
              name="phone"
              value={phone}
              placeholder={t("common.form.phone")}
              handleChange={onChange}
              errors={errors}
              formName={formName}
              transparent={false}
              noScroll
              Leading={() => (
                <Typography variant="title3" weight="medium" className="mb-0 mr-2" el="span">
                  {PHONE_PREFIX[locale.toUpperCase()]}
                </Typography>
              )}
            />
          </Col>
          {showJobType && (
            <Col xs={12}>
              <JobTypeSelector
                name="job_type"
                value={job_type}
                handleChange={onChange}
                errors={errors}
                formName={formName}
              />
            </Col>
          )}
          <Col xs={12} style={{ textAlign: "left" }}>
            <CheckBoxField
              name="accepts_offers"
              value={accepts_offers}
              label={t("common.form.accept_offers")}
              handleChange={onChange}
              errors={errors}
              formName={formName}
            />
          </Col>
          <Col xs={12} style={{ textAlign: "left" }}>
            <TermsAndPrivacyField
              name="accepts_conditions"
              value={accepts_conditions}
              handleChange={onChange}
              errors={errors}
              formName={formName}
              white={whiteLinks}
            />
          </Col>
        </Row>
        {showSubmit && (
          <Row>
            <ButtonContainer xs={12}>
              {isFetching ? (
                <AnimatedLoader margin="10px auto 0" />
              ) : (
                <PrimaryButton size="xxl" type="submit" className={submitClassName} shadow>
                  {!cta && <Icon color="white" Icon={Support} size="lg" className="mr-3" />}
                  {cta || t("common.we_call")}
                </PrimaryButton>
              )}
            </ButtonContainer>
          </Row>
        )}
      </form>
      {notification && <Notifications message={notification} setMessage={setNotification} variant="error" />}
    </Wrapper>
  )
}

CallMeForm.defaultProps = {
  showEmail: true,
  extraData: {},
  showSubmit: true,
  showJobType: false,
  handleClose: null,
  handleLoading: null,
  handleCompleted: null,
  cta: null,
  submitClassName: null,
  whiteLinks: true,
  initialData: {}
}

CallMeForm.propTypes = {
  formName: PropTypes.string.isRequired,
  showEmail: PropTypes.bool,
  extraData: PropTypes.object,
  showSubmit: PropTypes.bool,
  showJobType: PropTypes.bool,
  handleClose: PropTypes.func,
  handleLoading: PropTypes.func,
  handleCompleted: PropTypes.func,
  cta: PropTypes.string,
  submitClassName: PropTypes.string,
  whiteLinks: PropTypes.bool,
  initialData: PropTypes.object
}

export default CallMeForm
